import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { styled, useTheme } from '@mui/material/styles';
import { setGoToResetPassword } from 'store/appSlice';
import { useFetcher } from 'react-router-dom';
import { useFetcherStateForKey } from 'hooks/useNavigationState';
import { usePasswordResetInputHandler } from 'hooks/usePasswordResetInputHandler';
import PasswordResetProvider, {
  FORM_IDS,
} from 'providers/PasswordResetProvider';
import { usePasswordValidationCallback } from 'hooks/usePasswordValidationCallback';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import Box from '@mui/material/Box';
import Button from 'components/Button';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import PasswordTextfield from 'components/uncontrolled/PasswordTextfield';
import ErrorIcon from '@mui/icons-material/Error';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';

export default function UserResetPassword({ isPartner = false }) {
  const fetcher = useFetcher({ key: 'resetPassword' });
  const { success, error } = fetcher?.data ?? {};

  return (
    <PasswordResetProvider>
      <GridBox>
        <Header />
        <UserForm
          error={error}
          success={!!success}
          intentValue={
            isPartner ? 'reset-partner-password' : 'reset-user-password'
          }
        />
        <Footer />
      </GridBox>
    </PasswordResetProvider>
  );
}

UserResetPassword.propTypes = {
  isPartner: PropTypes.bool,
};

function UserForm({ error, success, intentValue }) {
  const fetcher = useFetcher({ key: 'resetPassword' });
  const isLoading = useFetcherStateForKey('resetPassword');
  const validationCallback = usePasswordValidationCallback();
  // eslint-disable-next-line no-unused-vars
  const { disabled, getError, handleBlur, handleChange, validationRef } =
    usePasswordResetInputHandler({
      ids: [
        FORM_IDS.CURRENT_PASSWORD,
        FORM_IDS.PASSWORD,
        FORM_IDS.CONFIRM_PASSWORD,
      ],
      validationCallback,
    });

  return (
    <fetcher.Form method="post" action="/" noValidate>
      <SuccessToast success={success} />
      <Content>
        {error && <ErrorMessage error={error} />}
        <Blurb>Create a new password below to reset your account.</Blurb>
        <PasswordTextfield
          autoComplete="password"
          error={getError(FORM_IDS.CURRENT_PASSWORD)}
          id={FORM_IDS.CURRENT_PASSWORD}
          label="Current Password"
          maxLength={50}
          onBlur={handleBlur(FORM_IDS.CURRENT_PASSWORD)}
          onChange={handleChange(FORM_IDS.CURRENT_PASSWORD)}
          defaultValue={''}
          placeholder="Enter your current password"
          required
          validationRef={validationRef}
        />
        <PasswordTextfield
          autoComplete="password"
          error={getError(FORM_IDS.PASSWORD)}
          id={FORM_IDS.PASSWORD}
          label="Password"
          maxLength={50}
          onBlur={handleBlur(FORM_IDS.PASSWORD)}
          onChange={handleChange(FORM_IDS.PASSWORD)}
          defaultValue={''}
          placeholder="Enter your password"
          required
          validationRef={validationRef}
        />
        {/* Confirm Password */}
        <PasswordTextfield
          autoComplete="password"
          error={getError(FORM_IDS.CONFIRM_PASSWORD)}
          id={FORM_IDS.CONFIRM_PASSWORD}
          label="Confirm Password"
          maxLength={50}
          onBlur={handleBlur(FORM_IDS.CONFIRM_PASSWORD)}
          onChange={handleChange(FORM_IDS.CONFIRM_PASSWORD)}
          defaultValue={''}
          placeholder="Enter your password again"
          required
          validationRef={validationRef}
        />
        <Button
          fullWidth
          name="intent"
          type="submit"
          value={intentValue}
          endArrow
          disabled={disabled || success}
          loading={isLoading}
          sx={{ mt: 1 }}
        >
          RESET PASSWORD
        </Button>
      </Content>
    </fetcher.Form>
  );
}

UserForm.propTypes = {
  error: PropTypes.any,
  success: PropTypes.bool,
  intentValue: PropTypes.string,
};

function SuccessToast({ success }) {
  const fetcher = useFetcher({ key: 'resetPassword' });
  const { announceSnackbar } = useSnackbarForMessage();
  const dispatch = useDispatch();

  useEffect(() => {
    if (success) {
      announceSnackbar(
        'Your password has been successfully updated.',
        'success'
      );
      dispatch(setGoToResetPassword({ goToResetPassword: false }));
    }

    // Return a cleanup function that gets called when the component unmounts
    return () => {
      if (success) {
        fetcher.submit({ idle: true }, { method: 'post', action: '/' });
      }
    };
  }, [success, announceSnackbar, dispatch, fetcher]);

  return null;
}

SuccessToast.propTypes = {
  success: PropTypes.bool,
};

function ErrorMessage({ error }) {
  let message =
    error?.data?.error?.message ||
    'An error occurred trying to reset the password. Please try again later.';

  if (
    error?.data?.error?.type === 'UNIFIED_AUTHENTICATION_FAIL' ||
    error?.data?.errorType === 'AUTHENTICATION_FAIL'
  ) {
    message = 'The current password is incorrect.';
  }
  return (
    <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
      <ErrorIconWrapper />
      <ErrorMessageText>{message}</ErrorMessageText>
    </Stack>
  );
}

ErrorMessage.propTypes = {
  error: PropTypes.any,
};

function Header() {
  const dispatch = useDispatch();
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing(1),
          backgroundColor: theme.palette.common.white,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            variant="body1"
            sx={{ color: theme.palette.custom.navyBlue.main }}
          >
            Reset Password
          </Typography>
        </Stack>
        <IconButton
          onClick={() => {
            dispatch(setGoToResetPassword({ goToResetPassword: false }));
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
    </>
  );
}

function Footer() {
  return (
    <>
      <Divider />
      <Box sx={{ height: '56px', width: '100%' }} />
    </>
  );
}

//##################################################################
// ### STYLED COMPONENTS
//##################################################################

const GridBox = styled(Box)`
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  height: 100%;
`;

const Content = styled((props) => (
  <Stack direction="column" gap={2} {...props} />
))(({ theme }) => ({
  flexGrow: 1,
  height: '100%',
  padding: theme.spacing(2),
}));

const Blurb = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const ErrorMessageText = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: theme.typography.fontWeightMedium,
}));

const ErrorIconWrapper = styled((props) => <ErrorIcon {...props} />)(
  ({ theme }) => ({
    color: theme.palette.error.main,
  })
);
